import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation, useLazyQuery } from '@apollo/react-hooks';
import DetailedTable from '../../components/DetailedTable';
import getColumns from './columns';
import {
  FETCH_NOTIFICATION_QUERY,
  DELETE_NOTIFICATION,
  FETCH_ALL_NOTIFICATION_QUERY,
  USER_DETAILS,
  RUN_NOTIFICATION_QUERY
} from '../graphQL/queries';
import { Loading } from '../../Loading';
import Edit from '@jda/lui-common-icon-library/ui/Edit';
import CreateNotificationDialog from './CreateNotificationDialog';
import { useParams } from 'react-router-dom';
import Trash from '@jda/lui-common-icon-library/ui/Trash';
import PlayCircle from '@jda/lui-common-icon-library/ui/PlayCircle';
import { useSnackbar } from 'notistack';
import { Button, ButtonGroup } from '@material-ui/core';
import { StateContext } from '../../App';
import ViewDetails from '@jda/lui-common-icon-library/ui/ViewDetails';
import toolTipInfo from '../../utils/tooltipInfo.json';
import Link from '@jda/lui-common-icon-library/ui/Link';

type specObj = {
  notificationName: string;
  timeSpan: number;
  thresholdValue: number;
  notificationMessage: string;
  thresholdType: string;
  thresholdLimit: number;
  thresholdMet: any;
  email: string;
  id: any;
  searchPeriod: any;
  filterRule: any;
  webhookUrl: string;
};

type responseObj = {
  GetNotificationData: specObj[];
};

const NotificationGrid = (props: any) => {
  const history = useHistory();
  const { workflowCode } = props;
  const { enqueueSnackbar } = useSnackbar();
  let { code } = useParams();
  const [runNotification] = useMutation(RUN_NOTIFICATION_QUERY);
  const { loading, data, refetch } = useQuery<responseObj, {}>(FETCH_NOTIFICATION_QUERY, {
    variables: { participant: code === undefined ? 'ADMIN' : code }
  });

  const [fetAllNotifications, { loading: resultLoading, data: resultData }] = useLazyQuery(
    FETCH_ALL_NOTIFICATION_QUERY
  );

  const { loading: userLoading, data: userData } = useQuery(USER_DETAILS, { fetchPolicy: 'cache-only' });
  const [deleteNotification] = useMutation(DELETE_NOTIFICATION);

  const [addNotification, setAddNotification] = React.useState(false);
  const [formInfo, setFormInfo] = React.useState<any>({});
  const [formData, setFormData] = React.useState<any>({});
  const [edit, isEdit] = React.useState<any>(false);
  const [rowId, setRowId] = React.useState<any>();
  const [filterCriteria, SetFilterCriteria] = React.useState<any>();
  const [selectedKey, setSelectedKey] = React.useState('RESULTS');
  const [userRole, setUserRole] = React.useState('');
  const state = useContext(StateContext);

  const columns: any = getColumns(state?.userPreferences?.timeZone || 'UTC');

  useEffect(() => {
    setUserRole(userData?.userProfile?.roles?.[0]);
    fetAllNotifications({
      variables: { jobStatus: 'NOTIFY', participant: code === undefined ? 'ADMIN' : code, page: 0 }
    });
  }, [userData]);

  const notificationTableActions: any = [
    selectedKey === 'RULE' &&
      (userRole === 'APP_ADMIN'
        ? {
            icon: () => <Edit data-id="edit-notification" />,
            onClick: (event: any, rowData: any) => handleEditNotification(rowData)
          }
        : {
            icon: () => <ViewDetails />,
            onClick: (event: any, rowData: any) => handleEditNotification(rowData),
            tooltip: 'View Details',
            position: 'row'
          }),
    selectedKey === 'RULE' &&
      userRole === 'APP_ADMIN' && {
        icon: () => <Trash data-id="delete-notification" />,
        onClick: (event: any, rowData: any) => handleDeleteNotification(rowData)
      },
    selectedKey === 'RULE' &&
      userRole === 'APP_ADMIN' && {
        icon: () => <PlayCircle data-id="run-notification" />,
        onClick: (event: any, rowData: any) => handleRunNotification(rowData)
      },
    selectedKey !== 'RULE' && {
      icon: () => <Link data-id="goto-events" />,
      onClick: (event: any, rowData: any) => handleEventUrl(rowData),
      tooltip: 'Go to events'
    },
    {
      icon: () => (
        <ButtonGroup size="small" aria-label="outlined primary button group">
          <Button
            data-id="Rule-ErrorTab"
            onClick={() => handleNotificationTabs('RESULTS')}
            variant={selectedKey === 'RESULTS' ? 'contained' : 'outlined'}
            color="primary"
          >
            Results
          </Button>
          <Button
            data-id="Results-ErrorTab"
            onClick={() => handleNotificationTabs('RULE')}
            variant={selectedKey === 'RULE' ? 'contained' : 'outlined'}
            color="primary"
          >
            Rule
          </Button>
        </ButtonGroup>
      ),
      isFreeAction: true,
      onClick: () => {}
    }
  ];

  const handleEventUrl = (rowData: any) => {
    const url = rowData?.eventUrl;
    if (code && workflowCode) {
      history.push(`/participant/${code}/service/${workflowCode}/errorDiagnosis?${url}`);
    } else {
      history.push(`errorDiagnosis?${url}`);
    }
  };

  const getData = (newPage: any) => {
    fetAllNotifications({
      variables: { jobStatus: 'NOTIFY', participant: code === undefined ? 'ADMIN' : code, page: newPage }
    });
  };

  const handleNotificationTabs = (key: any) => {
    setSelectedKey(key);
  };

  const getNotificationData = () => {
    if (selectedKey === 'RULE') {
      return !loading ? data?.GetNotificationData : [];
    } else if (selectedKey === 'RESULTS') {
      return !resultLoading ? resultData?.FetchAllNotifications?.content : [];
    }
  };

  const getNotificationCount = () => {
    if (selectedKey === 'RESULTS' && resultData?.FetchAllNotifications?.totalElements) {
      return resultData?.FetchAllNotifications?.totalElements | 0;
    }
  };

  const handleEditNotification = (rowData: any) => {
    setRowId(rowData.id);
    SetFilterCriteria(rowData.filterRule);
    setFormData({ ...rowData });
    isEdit(true);
    setAddNotification(true);
  };

  const handleDeleteNotification = (rowData: any) => {
    deleteNotification({
      variables: {
        ids: rowData.id
      }
    })
      .then(response1 => {
        refetch();
        enqueueSnackbar('Notification deleted successfully !!', { variant: 'success', preventDuplicate: true });
        return response1;
      })
      .catch(error => {
        enqueueSnackbar('Unable to delete', { variant: 'error', preventDuplicate: true });
      });
  };

  const handleRunNotification = (rowData: any) => {
    runNotification({
      variables: {
        notificationID: rowData.id
      }
    })
      .then(response1 => {
        refetch();
        enqueueSnackbar('Notification has been run successfully !!', { variant: 'success', preventDuplicate: true });
        return response1;
      })
      .catch(response1 => {
        enqueueSnackbar('Unable to run', { variant: 'error', preventDuplicate: true });
      });
  };

  const handleAddNotificationDialogClose = () => {
    setFormInfo({});
    setAddNotification(false);
  };

  const handleAddAuthenticationDialogSubmit = (formData: any) => {
    setAddNotification(false);
  };

  return (
    <div>
      <CreateNotificationDialog
        edit={edit}
        createNotificationDialog={addNotification}
        handleDialogClose={handleAddNotificationDialogClose}
        handleCreateNotificationDialogSubmit={handleAddAuthenticationDialogSubmit}
        formInfo={formInfo}
        formData={formData}
        refresh={refetch}
        rowId={rowId}
        filterRule={filterCriteria}
        participantCode={code}
        supportUser={userRole !== 'APP_ADMIN' ? true : false}
      />
      {loading && resultLoading ? (
        <Loading />
      ) : (
        <DetailedTable
          key={`Notification-${selectedKey}`}
          id="notificationGrid"
          title=""
          columns={columns[selectedKey]}
          data={getNotificationData()}
          showSelection={false}
          showPagination={true}
          showTopPagination={true}
          page={0}
          rowsPerPage={20}
          count={getNotificationCount()}
          tableActions={notificationTableActions}
          labelRowsPerPage={selectedKey === 'RULE' ? 'Rules Per Page' : 'Results Per Page'}
          pageLevelInfo={toolTipInfo?.pages?.notification}
          getData={getData}
          serverPagination
        />
      )}
    </div>
  );
};

export default NotificationGrid;
